import { useMemo } from "react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useCostSummary } from "@/hooks/recipes";
import { RecipeMethodHeader } from "@/components/recipes/method/RecipeMethodHeader";
import { RecipeSimpleCard } from "@/components/recipes/method/RecipeSimpleCard";
import { RecipeStagedSheet } from "@/components/recipes/method/RecipeStagedSheet";
import { scaledYield, type BatchScale } from "@/lib/recipes/staged-method";
import { useSessionMethod } from "./useSessionMethod";

/** Staged is the sheet with every step beside its ingredients; Simple is the card. */
export type MethodLayout = "staged" | "simple";

interface RecipeMethodPanelProps {
  recipeId: number;
  layout: MethodLayout;
  scale: BatchScale;
  onScaleChange: (scale: BatchScale) => void;
}

/**
 * The recipe body for a recipe on the screen at full size: the header with its
 * batch sizes, then the staged sheet or the simple card, as cook mode lays them
 * out. The batch lives with the screen, not here, so it survives moving between
 * tabs, the grid and the spotlight.
 */
export function RecipeMethodPanel({ recipeId, layout, scale, onScaleChange }: RecipeMethodPanelProps) {
  const { t } = useLingui();
  const { recipe, method, isError, retry } = useSessionMethod(recipeId);
  const { data: costSummary } = useCostSummary(recipeId);
  // Each line's weight and volume: extra detail, so the body draws without it.
  const measures = useMemo(
    () => new Map(costSummary?.lines.map((line) => [line.ingredientId, line.measures ?? null])),
    [costSummary],
  );

  if (isError) {
    return (
      <div
        role="alert"
        className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-(--color-danger-bg) px-4 py-3 text-sm text-(--color-danger-text)"
      >
        <span>{t`This recipe's method couldn't be loaded.`}</span>
        <Button type="button" size="sm" variant="outline" onClick={retry}>
          {t`Try again`}
        </Button>
      </div>
    );
  }
  if (!recipe || !method) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="flex flex-col gap-5">
      <RecipeMethodHeader recipe={recipe} scale={scale} onScaleChange={onScaleChange} showPrint={false} />
      {!method.isStaged && (
        <div className="rounded-xl bg-(--color-surface-muted) px-4 py-3 text-sm text-(--color-text-secondary)">
          {t`This recipe's method isn't split into stages yet, so everything is shown as one group.`}
        </div>
      )}
      {layout === "staged" ? (
        <RecipeStagedSheet method={method} scale={scale} yieldQuantity={recipe.yieldQuantity} measures={measures} />
      ) : (
        <RecipeSimpleCard
          method={method}
          scale={scale}
          yieldCount={scaledYield(recipe.yieldQuantity, scale)}
          measures={measures}
        />
      )}
    </div>
  );
}
